<template>
  <v-alert
    light
    border="left"
    elevation="2"
    colored-border
    :type="notificationType"
    v-bind="$attrs"
    :class="[$style.container, { [$style['container-full-width']]: fullWidth }]"
    @input="$emit('remove', { id, userEventId })"
  >
    <component
      :is="contentComponent"
      :main-type="mainType"
      :sub-type="subType"
      :message="message"
      :user-event-id="userEventId"
      @markReadAndRemove="$emit('remove', { id, userEventId })"
      @dismiss-dialog="$emit('dismiss-dialog')"
      @navigate-to-message-center="navigateToMessageCenter"
    />
    <template #prepend>
      <IconSelect :icon="resolvedIcon" height="24" width="24" class="ml-1 mr-3" />
    </template>
    <template v-if="timeout > 0" #append>
      <v-progress-circular class="ml-3" :rotate="360" size="24" :value="timeoutProgress" />
    </template>
  </v-alert>
</template>

<script>
import { getTypeParts } from '@/js/helper/messaging-helper.js'
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'
import { CONSTANTS as MESSAGE_CENTER_CONSTANTS } from '@/js/helper/message-center-constants.js'

export default {
  name: 'Notification',
  components: {
    IconSelect,
    AvailabilityNotification: () =>
      import(
        /* webpackChunkName: "AvailabilityNotification" */ '@/js/components/Notifications/AvailabilityNotification.vue'
      ),
    SystemNotification: () =>
      import(
        /* webpackChunkName: "SystemNotification" */ '@/js/components/Notifications/SystemNotification.vue'
      ),
    BlockingreasonNotification: () =>
      import(
        /* webpackChunkName: "BlockingreasonNotification" */ '@/js/components/Notifications/BlockingreasonNotification.vue'
      ),
    BackorderNotification: () =>
      import(
        /* webpackChunkName: "BackorderNotification" */ '@/js/components/Notifications/BackorderNotification.vue'
      ),
    TextNotification: () =>
      import(
        /* webpackChunkName: "TextNotification" */ '@/js/components/Notifications/TextNotification.vue'
      ),
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    timeout: {
      type: Number,
      default: -1,
    },
    type: {
      type: String,
      default: '',
    },
    notificationType: {
      type: String,
      default: 'info',
    },
    userEventId: {
      type: Number,
      default: null,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      timeoutProgress: undefined,
      runningTimeout: undefined,
    }
  },
  computed: {
    typeArray() {
      return getTypeParts(this.type)
    },
    mainType() {
      return this.typeArray.mainType
    },
    subType() {
      return this.typeArray.subType
    },
    contentComponent() {
      switch (this.mainType) {
        case 'availability':
          return 'AvailabilityNotification'
        case 'system':
          return 'SystemNotification'
        case 'blockingreason':
          return 'BlockingreasonNotification'
        case 'backorder':
          return 'BackorderNotification'
        default:
          return 'TextNotification'
      }
    },
    resolvedIcon() {
      if (this.icon) {
        return this.icon
      }
      switch (this.notificationType) {
        case 'success':
          return 'checkmark'
        case 'error':
          return 'warning'
        case 'warning':
          return 'warning-triangle'
        default:
          return 'info'
      }
    },
  },
  watch: {
    timeoutProgress() {
      this.doTimeoutProgress()
    },
  },
  created() {
    if (this.timeout > 0) {
      this.timeoutProgress = 0
    }
  },
  unmounted() {
    if (this.runningTimeout) {
      this.runningTimeout.cancel()
    }
  },
  methods: {
    doTimeoutProgress() {
      if (this.timeoutProgress >= 100) {
        this.runningTimeout = setTimeout(() => {
          this.$emit('remove', { id: this.id, userEventId: this.userEventId })
        }, this.timeout / 10)
        return
      }
      this.runningTimeout = setTimeout(() => {
        this.timeoutProgress = this.timeoutProgress + 10
      }, this.timeout / 10)
    },
    navigateToMessageCenter(tab = undefined) {
      const query = tab ? { tab } : {}

      this.$router
        .push({
          name: MESSAGE_CENTER_CONSTANTS.ROUTES.LIST,
          query,
        })
        // Catch to avoid "NavigationDuplicated" error from vue-router when navigating inside message center
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" module>
.container :global(.v-alert__content) {
  @media screen and (min-width: map-get($grid-breakpoints, 'sm')) {
    min-width: 25rem;
  }
  max-width: 25rem;
}

.container-full-width :global(.v-alert__content) {
  min-width: none;
  max-width: none;
}
</style>
